import {HttpRequest} from "@angular/common/http";

export const BYTES_PER_MB = 1048576;
export const MAX_FILE_SIZE_MB = 25;

// Keep in sync with FileTypeValidator.ALLOWED_EXTENSIONS on the backend, which rejects everything else
export const ALLOWED_FILE_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'svg', 'heic', 'heif', 'tif', 'tiff',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'dwg', 'dxf', 'ifc', 'rvt',
  'txt', 'md', 'rtf', 'html', 'csv', 'tsv'
];
export const ALLOWED_FORMATS_MESSAGE = `Allowed formats: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`;

/**
 * Value of the `accept` attribute of every file input. macOS and iOS grey out a file their own type registry
 * doesn't match with the listed MIME types (text/csv, a CAD drawing...), so the extensions are listed explicitly;
 * the image types make the iOS picker offer the photo library.
 */
const FILE_INPUT_ACCEPT_LIST = [
  ...ALLOWED_FILE_EXTENSIONS.map(extension => `.${extension}`),
  'image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/heic', 'image/heif', 'image/tiff'
].join(',');

/** iPhones, iPads (which report themselves as a Mac with a touch screen) */
export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;

  return /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * iOS turns every entry of `accept` into a system file type and greys out the files of any type it doesn't know
 * on the device (.dwg, .dxf, .ifc, .rvt have none), so there the filter is dropped: `null` removes the attribute.
 * What can be uploaded is still checked by pickUploadableFiles, which notifies about ALLOWED_FORMATS_MESSAGE.
 * Bind it with `[attr.accept]`, `[accept]` would set the text "null".
 */
export function buildFileInputAccept(isIos: boolean): string | null {
  return isIos ? null : FILE_INPUT_ACCEPT_LIST;
}

export const FILE_INPUT_ACCEPT = buildFileInputAccept(isIosDevice());

export const fileSizeErrorMessage = (maxFileSizeMb: number = MAX_FILE_SIZE_MB) =>
  `Error: Unable to upload. The file size must not exceed ${maxFileSizeMb}MB.`;
export const UPLOAD_FAILED_NOTIFICATION =
  'Error. Unable to upload. The connection was interrupted, please check your connection and try again';
export const UPLOAD_TOO_LARGE_NOTIFICATION =
  'Error. Unable to upload. The files are too large';

export function isAllowedFileFormat(fileName: string): boolean {
  const name = fileName.trim();
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return false;

  return ALLOWED_FILE_EXTENSIONS.includes(name.substring(dot + 1).toLowerCase());
}

/**
 * Splits the selected files into the ones that can be uploaded and the reasons for the rest, so that a file the
 * server would reject is never uploaded: `error` is the size message (shown under the file field),
 * `unsupportedFormat` tells that the caller has to notify about ALLOWED_FORMATS_MESSAGE.
 * Every file is uploaded by its own request, so only the size of a single file is limited.
 */
export function pickUploadableFiles(files: ArrayLike<File> | Iterable<File>,
                                    maxFileSizeMb: number = MAX_FILE_SIZE_MB): { accepted: File[], error: string | null, unsupportedFormat: boolean } {
  const accepted: File[] = [];
  let fileTooLarge = false;
  let unsupportedFormat = false;

  for (const file of Array.from(files)) {
    if (!isAllowedFileFormat(file.name)) {
      unsupportedFormat = true;
      continue;
    }

    if (file.size > maxFileSizeMb * BYTES_PER_MB) {
      fileTooLarge = true;
      continue;
    }

    accepted.push(file);
  }

  return {accepted, error: fileTooLarge ? fileSizeErrorMessage(maxFileSizeMb) : null, unsupportedFormat};
}

export function isFileUploadRequest(request: HttpRequest<unknown>): boolean {
  if (!(request.body instanceof FormData)) return false;

  let hasFile = false;
  request.body.forEach(value => hasFile ||= value instanceof File);
  return hasFile;
}

export const FILE_DISPLAY_NAME_MAX_LENGTH = 100;

export function fileNameWithoutExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const baseName = (dot > 0 ? fileName.substring(0, dot) : fileName).trim();
  return baseName.substring(0, FILE_DISPLAY_NAME_MAX_LENGTH);
}

export function fileExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.substring(dot) : '';
}

/** File name to save a document under: its display name with the extension of the stored file */
export function downloadFileName(displayName: string | null | undefined, storedFileName: string): string {
  const name = displayName?.trim();
  if (!name) return storedFileName;

  return name.replace(/[\\/:*?"<>|]/g, '_') + fileExtension(storedFileName);
}
